import { createFileRoute } from "@tanstack/react-router";
import { MathVisual } from "@/components/MathVisual";
import { ScaffoldNB } from "@/components/scaffolds";
import { mathExemplarToEngineItem, foundationItemToEngineItem } from "@/lib/engine/contentToEngineItem";
import mathExemplars from "@/curriculum/data/content/mathExemplars.json";
import foundationItems from "@/curriculum/data/content/foundationItems.json";
import { qaRoutesEnabled } from "@/lib/qaRoutes";

// QA-ITEM-ROUTE-SPEC: a PUBLIC (non-auth, no-PII) render of any math content
// item's question UI, so the build agent can navigate Chrome to
// `<preview>/qa-item?id=<id>` and SEE whether the manipulative renders and
// matches the prompt. Curriculum content only — no student data.
export const Route = createFileRoute("/qa-item")({
  validateSearch: (s: Record<string, unknown>) => ({ id: String(s.id ?? "") }),
  component: QaItem,
});

function QaItem() {
  // Hooks first (Rules of Hooks, cf. ADR-004): read search params before
  // any conditional early-return below.
  const { id } = Route.useSearch();
  // F4 belt-and-braces: even though App.jsx gates the /qa-item gate-ladder
  // bypass, the route component refuses to render the question/answer on
  // production deploys. This is the second independent layer so removing or
  // regressing the App.jsx gate cannot re-expose the answer key.
  // See docs/adr/026-qa-item-preview-only.md.
  if (!qaRoutesEnabled()) {
    return (
      <div className="p-8 text-lg" data-qa-item-blocked>
        QA route not available.
      </div>
    );
  }
  // MA-95: the route now resolves BOTH math packs — the spec is "any math
  // content item", and the foundation-math dispatch needs the same §3d
  // preview surface the exemplar fixes used. Same preview-only gate, same
  // no-PII curriculum-data-only surface.
  const rawExemplar = (mathExemplars as Array<Record<string, unknown>>).find(
    (m) => m.id === id,
  );
  const rawFoundation = rawExemplar
    ? undefined
    : (foundationItems as Array<Record<string, unknown>>).find((m) => m.id === id);
  const raw = rawExemplar ?? rawFoundation;
  if (!raw) {
    return (
      <div className="p-8 text-lg">
        QA: no item with id "{id}". Try ?id=ME0025
      </div>
    );
  }
  const item = rawExemplar
    ? mathExemplarToEngineItem(
        rawExemplar as never,
        (rawExemplar.targetSkill as string) ?? "ms.math.01",
      )
    : foundationItemToEngineItem(
        rawFoundation as never,
        (rawFoundation!.targetSkill as string) ?? "ms.math.02",
      );
  const mv = item.mathVisual;
  // MA-116: a scaffold-only item (no mathVisual) can still have a content-
  // bearing visualScaffold — preview it too, mirroring MathSession's
  // default-visibility fix, so the §3d verify surface matches what the
  // served session actually shows for these items.
  const scaffoldKind = !mv && item.visualScaffold?.kind === "missing_addend"
    ? item.visualScaffold.kind
    : null;
  return (
    <div className="sb-root min-h-screen p-6" data-qa-item={id}>
      <div className="mx-auto max-w-2xl space-y-6">
        <div
          className="rounded-xl bg-muted p-3 text-sm"
          data-qa-meta
          data-age-band={item.ageBand}
          data-visual={mv ? mv.kind : scaffoldKind ? `scaffold:${scaffoldKind}` : "NONE"}
        >
          id={id} · type={String(raw.itemType)} · ageBand={item.ageBand} ·
          answer={item.answer} · visual={mv ? mv.kind : scaffoldKind ? `scaffold:${scaffoldKind}` : "NONE"}
        </div>
        {mv ? (
          <div data-qa-visual="present" className="flex justify-center">
            <MathVisual
              kind={mv.kind}
              a={mv.a}
              b={mv.b}
              op={mv.op}
              total={mv.total}
              glyph={mv.glyph}
              arrangement={mv.arrangement}
              color={mv.color}
              shape={mv.shape}
              displayStyle={mv.displayStyle}
              sequence={mv.sequence}
            />
          </div>
        ) : scaffoldKind ? (
          <div data-qa-visual="present" className="flex justify-center">
            <ScaffoldNB data={item.visualScaffold!.data} />
          </div>
        ) : (
          <div data-qa-visual="none" className="text-center text-3xl font-bold">
            ⚠ NO VISUAL
          </div>
        )}
        <h2 className="text-center text-4xl font-bold">{item.prompt}</h2>
        <div className="grid grid-cols-2 gap-3">
          {(item.options ?? []).map((o) => (
            <div
              key={o}
              data-qa-option={o}
              className="rounded-2xl bg-muted p-4 text-center text-2xl font-bold"
            >
              {o}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
